import axios from "axios";
import {type SupportRequestReceipt, supportRequestReceiptOf} from "./supportRequestReceipt";

/**
 * What the api routes a request as (`SupportRequestCategory` in transferify-api-service).
 *
 * The api defaults an omitted category to `GENERAL`, so this stays optional — but the withdrawal paths
 * MUST send theirs, because that value is what makes the api acknowledge receipt to the consumer. A
 * statutory declaration filed without it is delivered to support and never acknowledged. `EU_WITHDRAWAL`
 * withdraws from the subscription; `EU_WITHDRAWAL_ONE_OFF` withdraws from one one-off purchase, which the
 * request then names (kitecorp/transferify-api-service#3181, kitecorp/transferify-ui#3967).
 */
export type SupportRequestCategory = "GENERAL" | "EU_WITHDRAWAL" | "EU_WITHDRAWAL_ONE_OFF";

/**
 * POST /support/request body (kitecorp/transferify-api-service#101).
 *
 * No identity field: the endpoint is AUTHENTICATED and the api reads the requester from
 * the Firebase bearer token the axios interceptor already attaches. A `replyTo` here would be a
 * second, spoofable answer to "who is asking" — which is exactly the difference between this endpoint
 * and the deliberately public feedback RPC (api-service#217).
 *
 * `reason` and `reasonText` (kitecorp/transferify-ui#3698) belong to a withdrawal declaration alone and
 * are present only when the consumer chose to give one (`account/withdrawal/withdrawalReason`'s
 * `withdrawalReasonFields`), so an api that predates them receives exactly the request it always did.
 */
export interface SupportRequest {
	subject: string;
	message: string;
	category?: SupportRequestCategory;
	/** A withdrawal declaration's optional reason; the api's `WithdrawalReason` names. */
	reason?: string;
	/** Its words for "Other", at most 500 characters. */
	reasonText?: string;
	/**
	 * The purchase a `EU_WITHDRAWAL_ONE_OFF` declaration is for: its family as the api's enum spells it, and its id.
	 * The api answers 400 to that category without them and to any other category with them.
	 */
	purchaseKind?: "OVER_LIMIT_ALLOWANCE" | "PERSONALIZATION_CREDIT";
	purchaseId?: string;
}

/**
 * Support-request service (house `*Api.ts` axios pattern, see `account/AccountApi.ts`). Holds the
 * single HTTP call the {@link useSupportRequest} hook drives, so the dialog view stays dumb (epic #26).
 */
export class SupportRequestApi {
	/**
	 * POSTs the request to `/support/request`. Resolves with the api's receipt on 200, rejects on any
	 * transport/HTTP error — `supportRequestFailure.ts` turns that rejection into the outcome the user
	 * is shown.
	 *
	 * The text is posted VERBATIM: the api measures the raw string its `@Size` bounds are written
	 * against, so trimming here would validate one string and send a different one.
	 */
	static async send(request: SupportRequest): Promise<SupportRequestReceipt> {
		const response = await axios.post("/support/request", request);
		return supportRequestReceiptOf(response.data);
	}
}
